import React, { useState } from 'react';

import { mountRootParcel } from 'single-spa';
import Parcel from 'single-spa-react/parcel';

import { createReactRenderer } from 'helpers/react-renderer';
import { getLocationId } from 'helpers/session';
import { InvoicingConfig } from 'root/apps/QuoteMachineIntegration/common';
import { CustomerCreditAccountWebComponentHost } from 'root/apps/QuoteMachineIntegration/components/CustomerProfileWebComponents';
import { usePayBalance } from 'root/apps/QuoteMachineIntegration/components/PayBalance';
import { useAutoARSettings } from 'root/apps/QuoteMachineIntegration/hooks/useAutoARSettings';
import AccountStatementService from 'root/apps/QuoteMachineIntegration/services/AccountStatementService';
import AutomatedAccountReceivablesService from 'root/apps/QuoteMachineIntegration/services/AutomatedAccountReceivablesService';
import CustomerAccountService from 'root/apps/QuoteMachineIntegration/services/CustomerAccountService';
import CustomerService from 'root/apps/QuoteMachineIntegration/services/CustomerService';
import LocationService from 'root/apps/QuoteMachineIntegration/services/LocationService';
import InvoicingCustomer from 'root/apps/QuoteMachineIntegration/state/Customer';
import { CustomerAccount } from 'root/apps/QuoteMachineIntegration/state/CustomerAccount';
import { CustomerCreditAccountDetailsHeadingProps } from 'root/apps/QuoteMachineIntegration/state/CustomerCreditAccountDetailsHeadingProps';
import Location from 'root/apps/QuoteMachineIntegration/state/Location';
import { SystemJS } from 'root/apps/QuoteMachineIntegration/SystemJS';
import { hasCreditAccountId } from 'root/apps/QuoteMachineIntegration/utils/creditAccountId';
import { getSessionData } from 'root/apps/QuoteMachineIntegration/utils/SessionData';
import Modules from 'root/apps/shared_modules/Modules';
import { datadogRum } from 'root/datadog/datadogRum';

import { AccountStatementFormData } from '../../state/AccountStatementFormData';
import { AccountStatementOption } from '../../state/AccountStatementOption';
import { AccountStatementSendPayload } from '../../state/AccountStatementSendPayload';
import { getProposalPreviewURL } from '../../utils/ProposalPreviewConfig';

export enum ToastNotificationTypes {
  SUCCESS = 'success',
  NEGATIVE = 'negative',
}

export enum ToastNotificationAction {
  CREATE_STATEMENT_ERROR = 'create-statement-error',
  SEND_STATEMENT_ERROR = 'send-statement-error',
  SEND_STATEMENT_SUCCESS = 'send-statement-success',
}

const CustomerCreditAccountDetailsHeadingParcel = ({
  customerID,
  accountID,
  customerFullName,
  customerEmail,
  currentCreditLimit,
  creditAccountID,
  isCustomerArchived,
  employeeHasPermissionsToEditCreditLimit,
}: CustomerCreditAccountDetailsHeadingProps) => {
  const {
    loading: loadingAutoArSettings,
    error: autoArError,
    accountStatus: autoArAccountStatus,
    customerStatus: autoArCustomerStatus,
  } = useAutoARSettings(creditAccountID.toString());

  const [accountStatementModalActionLoading, setAccountStatementModalActionLoading] =
    useState(false);

  const { mountPayBalance, unmountPayBalance } = usePayBalance({
    accountID,
    customerID,
    creditAccountID,
    customerName: customerFullName,
    customerEmail,
  });

  const handleEditCreditLimit = async (amount: string) => {
    const data = await CustomerAccountService.createOrUpdateCustomerCreditAccount(
      customerID,
      amount
    );

    if (data) {
      window.location.reload();
    }
  };

  // eslint-disable-next-line consistent-return
  const submitAccountStatementModal = async (formData: AccountStatementFormData) => {
    setAccountStatementModalActionLoading(true);

    const { option } = formData;

    if (option === AccountStatementOption.SEND) {
      const payload: AccountStatementSendPayload = {
        emailMethod: {
          recipientAddresses: [formData.email],
        },
        ...(formData.invoicesDueDateRange
          ? { invoicesDueDateRange: formData.invoicesDueDateRange }
          : {}),
      };

      if (!autoArError) {
        AutomatedAccountReceivablesService.processAutoArOptions(
          formData.automatedAccountReceivablesOptions,
          creditAccountID.toString()
        );
      }

      try {
        const { status, statementIdentifier } =
          await AccountStatementService.createAndSendAccountStatement(
            creditAccountID.toString(),
            customerID,
            payload
          );

        if (status === 202) {
          return {
            type: ToastNotificationTypes.SUCCESS,
            action: ToastNotificationAction.SEND_STATEMENT_SUCCESS,
            accountStatementIdentifier: statementIdentifier,
          };
        }

        return {
          type: ToastNotificationTypes.NEGATIVE,
          action: ToastNotificationAction.SEND_STATEMENT_ERROR,
        };
      } catch (err) {
        return {
          type: ToastNotificationTypes.NEGATIVE,
          action: ToastNotificationAction.SEND_STATEMENT_ERROR,
        };
      } finally {
        setAccountStatementModalActionLoading(false);
      }
    }
  };

  return (
    <Parcel
      mountParcel={mountRootParcel}
      skipCSSUnmount={true}
      sessionData={getSessionData()}
      handleError={(err: Error) => datadogRum.addError(err)}
      customerFullName={customerFullName}
      customerEmail={customerEmail}
      currentCreditLimit={currentCreditLimit}
      isCustomerArchived={isCustomerArchived}
      creditAccountID={creditAccountID}
      employeeHasPermissionsToEditCreditLimit={employeeHasPermissionsToEditCreditLimit}
      onEditCreditLimit={handleEditCreditLimit}
      onSubmitAccountStatementModal={submitAccountStatementModal}
      accountStatementModalActionLoading={
        accountStatementModalActionLoading || loadingAutoArSettings
      }
      isAutoARSettingsLoading={loadingAutoArSettings}
      showAutomatedAccountReceivablesWidget={!loadingAutoArSettings && !autoArError}
      automatedAccountReceivablesOptions={{
        customerEnabled: autoArCustomerStatus,
        enabledByDefault: autoArAccountStatus,
      }}
      getCreditAccountDetails={() =>
        CustomerAccountService.getCustomerAccount(
          creditAccountID?.toString() ?? '',
          currentCreditLimit ?? '0'
        )
      }
      getProposalPreviewURL={async (creditAccountDetails: CustomerAccount | null) => {
        const locationId = getLocationId();
        const locationData = await LocationService.getLocation(locationId);
        const location = new Location(locationData);
        const customerData = await CustomerService.getCustomer(customerID);
        const customerDetails = new InvoicingCustomer(customerData);
        // Use invoicing contact UUID from already-fetched credit account details (from getCreditAccountDetails)
        const invoicingCreditAccountId = creditAccountDetails?.invoicingContactId;
        if (!invoicingCreditAccountId || invoicingCreditAccountId === 'undefined') {
          throw new Error('Invoicing credit account ID is required for account statement preview');
        }
        // accountRef must be the docker-retail account ID (same as accountID); do not use creditAccountID
        // Cutoff is determined by BE (time.Now()); FE does not send cutoffDate.
        return {
          url: getProposalPreviewURL(),
          accountRef: accountID != null ? String(accountID) : '',
          objectData: {
            creditAccountID: invoicingCreditAccountId,
            integrationKey: 'ls-retail',
            location,
            customerDetails,
          },
        };
      }}
      config={async () =>
        (await SystemJS.import<InvoicingConfig>('@lightspeed/invoicing')).default
          .CustomerCreditAccountDetailsHeadingConfig
      }
      mountPayBalance={mountPayBalance}
      unmountPayBalance={unmountPayBalance}
    />
  );
};

const CustomerCreditAccountDetailsHeading = (props: CustomerCreditAccountDetailsHeadingProps) => {
  if (
    Modules.hasModule('customers.invoicing_integration.customer_profile_web_components') &&
    hasCreditAccountId(props.creditAccountID)
  ) {
    return (
      <CustomerCreditAccountWebComponentHost
        customerID={props.customerID}
        accountID={props.accountID}
        customerFullName={props.customerFullName}
        customerEmail={props.customerEmail}
        currentCreditLimit={props.currentCreditLimit}
        creditAccountID={props.creditAccountID}
        isCustomerArchived={props.isCustomerArchived}
        employeeHasPermissionsToEditCreditLimit={props.employeeHasPermissionsToEditCreditLimit}
        lsrBalanceOwed={props.lsrBalanceOwed}
        lsrDepositBalance={props.lsrDepositBalance}
      />
    );
  }

  return <CustomerCreditAccountDetailsHeadingParcel {...props} />;
};

const RenderedComponent = createReactRenderer({
  component: CustomerCreditAccountDetailsHeading,
  selector: '[data-react-root="CustomerCreditAccountDetailsHeading"]',
});

export default CustomerCreditAccountDetailsHeading;

history.onChangeState(
  [
    {
      name: ['customer.views.customer'],
      form_name: 'view',
      tab: 'account',
      id: undefined,
    },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
